import axios, { AxiosRequestConfig } from "axios";
import { HttpMethod, Endpoint, EndpointMap } from "./interfaces";

// docs/security-and-platform-work-plan.md Этап 7 — the access/refresh
// tokens are HttpOnly cookies now; PersonalData.tsx and others build a lot
// of their own axios configs directly instead of going through
// generateRequestConfig() below (which already sets this per-call). A
// global default makes every such call send the cookie too, without
// auditing each call site individually.
axios.defaults.withCredentials = true;

// general configuration
export const projectName = "pd.id.express";
export const projectApiUrl = process.env.REACT_APP_API_URL;
export const dictionaryApiUrl =
  process.env.REACT_APP_GUN_DICTIONARY_API_BASE_URL || "https://qa-api-dictionary.id.express/v1/dictionaries";
export const projectBasename = "/"; // base path 
export const defaultLanguage = "en";

export const cookieSameSite = (process.env.REACT_APP_COOKIE_SAMESITE ?? "Lax") as "Lax" | "Strict" | "None";
export const cookieDomain = process.env.REACT_APP_COOKIE_DOMAIN || undefined;
export const cookieSecure = process.env.REACT_APP_COOKIE_SECURE?.toLowerCase() === "true";
export const cookieHttpOnly = process.env.REACT_APP_COOKIE_HTTPONLY?.toLowerCase() === "true";
const authCookieNamespace = projectName.replace(/[^a-z0-9]+/gi, "-").replace(/^-|-$/g, "").toLowerCase();
export const authAccessTokenCookieName = `${authCookieNamespace}-access-token`;
export const authRefreshTokenCookieName = `${authCookieNamespace}-refresh-token`;



// apiEndpoints
const endpointMap: EndpointMap = {
  personal_data: `${projectApiUrl}/personal_data`,
  personal_data_attachments: `${projectApiUrl}/personal_data/attachments`,
  languages: `${dictionaryApiUrl}?type=language&fields=value.default.name,metadata.flag,metadata.iso_639_1`,
  countries: `${dictionaryApiUrl}?type=country`,
  regions: `${dictionaryApiUrl}?type=region`,
  cities: `${dictionaryApiUrl}?type=city`,
  timezones: `${dictionaryApiUrl}?type=timezone&fields=value.default.name`,
  apps: `${dictionaryApiUrl}?type=app&fields=_id,key,status,metadata`,
  get_csrf_token: `${projectApiUrl}/get_csrf_token`,
  onetimetoken: `${projectApiUrl}/onetimetoken`,
  // docs/security-and-platform-work-plan.md Этап 7 — now served locally
  // (api-pd.id.express registers the same shared Refresh resource api.id.express
  // uses), same-origin, so the HttpOnly refresh cookie set on this response
  // is actually sendable back on the next call.
  refresh: `${projectApiUrl}/refresh`,
};

// configuration for Axios

/**
 * Generates a dynamic Axios request configuration object.
 *
 * @param {HttpMethod} method - The HTTP method (e.g., "get", "post", "put", "delete").
 * @param {Endpoint} endpoint - The API endpoint name (e.g., "login", "registration").
 * @param {Object} data - The request payload data (used in the request body).
 * @param {Object} headers - Additional headers to be included in the request.
 * @param {Object} params - URL parameters to be included in the request.
 * @returns {AxiosRequestConfig} - The Axios request configuration object.
 */
export const generateRequestConfig = (
  method: HttpMethod,
  endpoint: Endpoint,
  data: object,
  headers: Record<string, string | null> = {},
  params: Record<string, string | number | null | undefined> = {} 
): AxiosRequestConfig => {
  const url = endpointMap[endpoint];

  if (!url) {
    throw new Error(`Invalid endpoint: ${endpoint}`);
  }

  return {
    method,
    url,
    headers: {
      "X-Requested-With": "XMLHttpRequest",
      ...headers,
    },
    data,
    params,
    withCredentials: true 
  };
};

// validation
export const MIN_PASSWORD_LENGTH = 8;
export const MAX_PASSWORD_LENGTH = 32;
